import { isNullOrUndefined, isUndefined } from "util";
import {ITranslatedObj} from "../models/common";
/**
 * This function return a new instance of object target and assign the params value
 * @param target
 * @param source
 * @returns {any}
 */
export function tassign<T extends U, U>(target: T, ...source: U[]): T {
  return Object.assign({}, target, ...source);
}

/**
 * This function coerces a string into a string literal type.
 * Using tagged union types in TypeScript 2.0, this enables
 * powerful typechecking of our reducers.
 *
 * Since every action label passes through this function it
 * is a good place to ensure all of our action labels
 * are unique.
 */

const typeCache: { [label: string]: boolean } = {};
export function type<T>(label: T | ""): T {
  if (typeCache[<string>label]) {
    throw new Error(`Action type "${label}" is not unique"`);
  }

  typeCache[<string>label] = true;

  return <T>label;
}

/**
 * This function remove duplicate elements in an array and return the new array
 * without duplicate elements
 */
export function removeDuplicate<T>(arr: T[]): T[] {
  return arr.reduce<T[]>(
    (acc: T[], cur: T) =>
      acc.find(val => val === cur) === cur ? acc : acc.concat(cur),
    []
  );
}

export class Utils {
  /**
   * This function normalize and capitalize a string, each underscore
   * will be converted in space and each word will be capitalized
   * @param str input string to capitalize
   * @returns {string} capitalized and normalized string
   */
  static capitalize(str: string): string {
    return str
      .replace(/_/g, " ")
      .split(" ")
      .map(v => v.charAt(0).toUpperCase() + v.slice(1))
      .join(" ");
  }

  static keyCompose(val1: string, val2: string): string {
    return Utils.stringCompose(val1, val2).toLowerCase();
  }

  static stringCompose(val1: string, val2: string, sep: string = ": "): string {
    const v1 = isNullOrUndefined(val1) || val1 === "" ? "" : val1; // (!isNullOrUndefined(val1) || val1 != "") ? val1 : "";
    const v2 =
      isNullOrUndefined(val2) || val2 === ""
        ? ""
        : (v1 !== "" ? sep : "") + val2; // (!isNullOrUndefined(val2) || val2 != "") ? sep+val2 : "";
    return v1 + v2;
  }

  static isValueReal(x: number): boolean {
    return !isNullOrUndefined(x) && isFinite(x);
  }
  static isValueEmpty(x: number): boolean {
    return !Utils.isValueReal(x);
  }
  static isInRange(x: number, min: number, max: number) {
    return Utils.isValueReal(x) && min <= x && x <= max;
  }

  static acceptInRange(
    value: number,
    min: number,
    max: number,
    prefix: string,
    suffix: string,
    verb: string = "must"
  ): ITranslatedObj {
    if (
      Utils.isValueEmpty(min) ||
      min === Number.NEGATIVE_INFINITY ||
      min === Number.MIN_VALUE
    ) {
      // quando è ovvio che pe ogni possibile "value" vale: min <= value
      // cioè quando "min <= value" è staticamente vera...
      return Utils.acceptUpTo(value, max, prefix, suffix, false, verb);
    }

    if (
      Utils.isValueEmpty(max) ||
      max === Number.POSITIVE_INFINITY ||
      max === Number.MAX_VALUE
    ) {
      // quando è ovvio che pe ogni possibile "value" vale: value <= max
      // cioè quando "value <= max" è staticamente vera...
      return Utils.acceptGreaterThan(value, min, prefix, suffix, verb);
    }

    if (!(min <= value && value <= max)) {

      const key = prefix === 'number of led in series' || prefix === 'number of strings' ? prefix : 'accept in range';

      if (min === max) {
        return {
          key: `${key} equal_${verb}`,
          data: { prefix, suffix, min }
        };
      }
      return {
        key: `${key} from_${verb}`,
        data: { prefix, suffix, min, max }
      };
    }
    return null;
  }

  static acceptInArray(
    value: number,
    values: number[],
    prefix: string,
    suffix: string,
    verb: string = "must",
  ): ITranslatedObj {
    if (!values.includes(value)) {
        return {
          key: 'accept in array',
          data: { prefix, verb, suffix, values: values.join(', ') }
        };
    }
    return null;
  }


  // static acceptInRangeOpen(
  //   value: number,
  //   min: number,
  //   max: number,
  //   prefix: string,
  //   suffix: string,
  //   rangeOpen: string = "lr"
  // ): string {
  //   let str: string = null;
  //   switch (rangeOpen) {
  //     case "l":
  //       if (value <= min || value > max) {
  //         str = prefix + " must be &gt;" + min + " and ≤ " + max + " " + suffix;
  //       }
  //       break;
  //     case "r":
  //       if (value < min || value >= max) {
  //         str = prefix + " must be ≥" + min + " and &lt; " + max + " " + suffix;
  //       }
  //       break;
  //     case "lr":
  //     default:
  //       if (value <= min || value >= max) {
  //         str =
  //           prefix + " must be &gt; " + min + " and &lt; " + max + " " + suffix;
  //       }
  //       break;
  //   }
  //   return str;
  // }

  static acceptUpTo(
    value: number,
    max: number,
    prefix: string,
    suffix: string,
    excluded: boolean = false,
    verb: string = "must"
  ): ITranslatedObj {
    if (
      Utils.isValueEmpty(max) ||
      max === Number.POSITIVE_INFINITY ||
      max === Number.MAX_VALUE
    ) {
      // quando è ovvio che pe ogni possibile "value" vale: value <= max
      // cioè quando "value <= max" è staticamente vera...
      return null;
    }

    let ret: any = null;

    let op: string;
    let isUpTo: () => boolean;

    if (!excluded) {
      // INCLUDED
      op = "≤";
      isUpTo = () => value <= max;
    } else {
      // EXCLUDED
      op = "&lt;";
      isUpTo = () => value < max;
    }

    if (Utils.isValueEmpty(value) || !isUpTo()) {
      const key = prefix === 'number of led in series' || prefix === 'number of strings' ? `${prefix}_` : 'accept up to_';
      ret = {
        key: `${key}${verb}`,
        data: { prefix, suffix, op, max}
      };
    }

    return ret;
  }

  static acceptGreaterThan(
    value: number,
    min: number,
    prefix: string,
    suffix: string,
    verb: string = "must"
  ): ITranslatedObj {
    if (
      Utils.isValueEmpty(min) ||
      min === Number.NEGATIVE_INFINITY ||
      min === Number.MIN_VALUE
    ) {
      // quando è ovvio che pe ogni possibile "value" vale: min <= value
      // cioè quando "min <= value" è staticamente vera...
      return null;
    }

    return Utils.isValueEmpty(value) || value < min
      ? {
        key: verb === 'must' ? 'accept greater than_must' : 'accept greater than_should',
        data: { prefix, suffix, min}
      }
      : null;
  }

  static isNumberRequired(value: number, prefix: string): string {
    return Utils.isValueEmpty(value) ? prefix + " is required" : null;
  }

  static acceptNotNegative(value: number, prefix: string): string {
    return Utils.isValueEmpty(value) || value < 0
      ? prefix + " must be not negative"
      : null;
  }

  static acceptPositive(value: number, prefix: string): string {
    return Utils.isValueEmpty(value) || value <= 0
      ? prefix + " must be positive"
      : null;
  }

  static getNumberDefaultable(e: number, d: number) {
    return Utils.isValueReal(e) ? e : d;
  }

  static Value(e: number, d: number = 0) {
    return Utils.getNumberDefaultable(e, d);
  }

  static getFieldValue(obj: any, chain: string, def = null): any {
    if (isNullOrUndefined(obj)) { return def; }

    let field: any = obj;
    const fieldChain = chain.split(".");

    const fullMatch: boolean = fieldChain.every(
      fieldName => !isUndefined((field = field[fieldName]))
    );

    // fieldChain.reduce((prev:any, curr:string)=>{return isNullOrUndefined(prev[curr])? def : prev[]})

    return fullMatch ? field : def;
  }

  /**
   * Compara due oggetti in profendità
   */
  static isObjectEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  // LZW-compress a string
  public static lzw_encode(s: string): string {
    const dict = {};
    // let data = (s + "").split(""); //--- this fail for unicode chars ?!?
    // let data = (s + "").split("").map(x=> "%"+ x.charCodeAt(0).toString(16).toUpperCase()).join("").split("");
    const data = (s + "")
      .split("")
      .map(
        x =>
          x === "%" || x.charCodeAt(0) > 255
            ? "%" +
              x
                .charCodeAt(0)
                .toString(16)
                .toUpperCase() +
              "%"
            : x
      )
      .join("")
      .split("");
    const out = [];
    let currChar;
    let phrase = data[0];
    let code = 256;

    for (let i = 1; i < data.length; i++) {
      currChar = data[i];
      if (dict["_" + phrase + currChar] != null) {
        phrase += currChar;
      } else {
        out.push(phrase.length > 1 ? dict["_" + phrase] : phrase.charCodeAt(0));
        dict["_" + phrase + currChar] = code;
        code++;
        phrase = currChar;
      }
    }
    out.push(phrase.length > 1 ? dict["_" + phrase] : phrase.charCodeAt(0));
    for (let i = 0; i < out.length; i++) {
      out[i] = String.fromCharCode(out[i]);
    }
    return out.join("");
  }

  // Decompress an LZW-encoded string
  public static lzw_decode(s: string): string {
    const dict = {};
    const data = (s + "").split("");
    let currChar = data[0];
    let oldPhrase = currChar;
    const out = [currChar];
    let code = 256;
    let phrase;
    for (let i = 1; i < data.length; i++) {
      const currCode = data[i].charCodeAt(0);
      if (currCode < 256) {
        phrase = data[i];
      } else {
        phrase = dict[currCode] ? dict[currCode] : oldPhrase + currChar;
      }
      out.push(phrase);
      currChar = phrase.charAt(0);
      dict[code] = oldPhrase + currChar;
      code++;
      oldPhrase = phrase;
    }
    // return out.join("");
    // return out.join("").split("%").slice(1).map(x=>String.fromCharCode(parseInt(x,16))).join("");
    const invalidCharMatch = new RegExp("%[0-9A-F]{1,4}%", "ig"); // Unicode chars and %
    return out
      .join("")
      .replace(invalidCharMatch, str =>
        String.fromCharCode(parseInt(str.slice(1), 16))
      );
  }
}

export function numberToJson(p: number|null|undefined): string {
  return p ? p.toString() : 'NaN';
}

export function numberfromJson(strVal: string|null|undefined): number {
  return strVal ? Number.parseFloat(strVal) : NaN;
}

function support_links(lang: string= "en") {
  return {
    home_st: `https://www.st.com/content/st_com/${lang}.html`,
    help: "/help/#/introducing",
    video_tutorial: "https://youtu.be/mEeZxco1ab4",
    release_note: "/release_note/#/releasenote",
    forum: "https://stcommunity.st.com/t5/other-software/bd-p/other-software-forum",

    my_st_com: `https://my.st.com/content/my_st_com/${lang}.html`,
    my_space: `https://my.st.com/content/my_st_com/${lang}/my-space/my-preferences.html`,
    my_profile: `https://my.st.com/content/my_st_com/en/my-space/my-preferences.html`,
    my_preferences: `https://my.st.com/content/my_st_com/${lang}/my-space/my-preferences.html`,
    terms_of_use: `https://www.st.com/content/st_com/${lang}/common/terms-of-use.html`,
    privacy_portal: `https://www.st.com/content/st_com/${lang}/common/privacy-policy.html`,
    contact_us: `https://www.st.com/content/st_com/${lang}/contact-us.html`,
    manage_cookies: `https://www.st.com/content/st_com/${lang}/common/cookies-policy.html`,
    exercise_rights: `https://app-de.onetrust.com/app/#/webform/2b87200d-4023-4588-9df7-ab0cdea1a67e`,

    product_folder: `https://www.st.com/${lang}/product/`,
    datasheet: `https://www.st.com/resource/${lang}/datasheet/`,
    databrief: `https://www.st.com/resource/${lang}/data_brief/`,

    // Used when eDS has no solutions.
    stsales_offices: `https://www.st.com/content/st_com/${lang}/contact-us.html`,
    distributors: `https://www.st.com/content/st_com/${lang}/contact-us.html`,
    power_management: `https://www.st.com/${lang}/power-management.html`,
    online_sales_support: `https://www.st.com/content/st_com/${lang}/support/online-support.html`,
    product_selector: `https://www.st.com/content/st_com/${lang}/product-selector-welcome.html`,
    console_home: '/',
  };
}

export function support_to_url(req_supp: string, lang: string = "en" ) {
  const support_lang = {
    en: "en",
    zh: "zh",
    ja: "ja",
  };
  const str = "" + req_supp.toLowerCase();
  const supLang = support_lang[lang] || "en";
  const _support_links = support_links(supLang);
  return _support_links[str] || _support_links["home_st"];
}

